import React, { useRef, useState } from "react";
import { Row, Col, Card, Typography, Button } from "antd";
import QrCameraScanner from "./QrCameraScanner";
import CccdResultInfo from "./CccdResultInfo";
import moment from "moment";
import { isValidCccdQr } from "helper/commonValidator";
import { CameraOutlined } from "@ant-design/icons";
import "./style.scss";

// Tách parseCccdQr để dùng lại
function parseCccdQr(qrText) {
  const arr = qrText.split("|");
  if (arr.length < 6) return {};
  return {
    cccdNumber: arr[0],
    cmndNumber: arr[1],
    fullName: arr[2],
    dob: arr[3] ? moment(arr[3], "DDMMYYYY").format("DD/MM/YYYY") : "",
    gender: arr[4],
    address: arr[5],
    createdAt: arr[6] ? moment(arr[6], "DDMMYYYY").format("DD/MM/YYYY") : "",
  };
}

export default function QrCccdScanner() {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Khi tự động phát hiện QR
  function handleQrDetected(qrText, imgUrl) {
    setResult(parseCccdQr(qrText));
    setCapturedImage(imgUrl);
    setIsCameraOpen(false);
    setError(null);
  }

  // Khi chụp thủ công
  function handleManualCapture(imgUrl) {
    setCapturedImage(imgUrl);
    setIsCameraOpen(false);
    setResult(null);
    setError(null);
    scanCapturedImage(imgUrl);
  }

  // Quét QR từ ảnh đã chọn hoặc vừa chụp thủ công
  async function scanCapturedImage(imgDataUrl) {
    setResult(null);
    setError(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const qrRegionId = "qr-reader-region-img";
      if (document.getElementById(qrRegionId)) {
        document.getElementById(qrRegionId).innerHTML = "";
      }
      const res = await fetch(imgDataUrl);
      const blob = await res.blob();
      const file = new File([blob], "capture.png", { type: "image/png" });
      const html5Qr = new Html5Qrcode(qrRegionId);
      await html5Qr
        .scanFile(file, true)
        .then((decodedText) => {
          if (!isValidCccdQr(decodedText)) {
            setError("Mã QR không hợp lệ");
            return;
          }
          setResult(parseCccdQr(decodedText));
          setError(null);
          html5Qr.clear();
        })
        .catch(() => {
          setResult(null);
          setError(
            "Không quét được mã QR trên ảnh này. Hãy đảm bảo mã QR rõ nét, đủ sáng, nằm trong vùng CCCD."
          );
          html5Qr.clear();
        });
    } catch (e) {
      setError("Lỗi khi scan ảnh QR.");
    }
  }

  // Chọn ảnh từ file
  async function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCapturedImage(reader.result);
      scanCapturedImage(reader.result);
    };
    reader.readAsDataURL(file);
    setIsCameraOpen(false);
    setResult(null);
    setError(null);
  }

  // Đổi/chụp lại ảnh
  function retake() {
    setCapturedImage(null);
    setResult(null);
    setError(null);
    setIsCameraOpen(false);
    fileInputRef.current.value = "";
  }

  return (
    <div className="qr-cccd-scanner">
      <Row gutter={32}>
        <Col
          className={`qr-cccd-scanner__camera-section ${
            capturedImage ? "hidden" : ""
          }`}
          xs={24}
          lg={12}
        >
          <Card
            title={
              <Typography.Title
                level={4}
                style={{ margin: 0, textAlign: "center" }}
              >
                Quét hình ảnh CCCD
              </Typography.Title>
            }
            bordered={false}
            style={{ minHeight: 580 }}
            bodyStyle={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 500,
              padding: 0,
            }}
          >
            {!capturedImage && (
              <QrCameraScanner
                isCameraOpen={isCameraOpen}
                onCapture={handleManualCapture}
                onQrDetected={handleQrDetected}
                onCloseCamera={() => setIsCameraOpen(false)}
              />
            )}
            {!isCameraOpen && (
              <Button
                style={{ marginTop: 12 }}
                onClick={() => {
                  retake();
                  setIsCameraOpen(true);
                }}
                type="primary"
                icon={<CameraOutlined />}
                size="large"
              >
                Bật camera
              </Button>
            )}
            <Button
              type="default"
              size="large"
              onClick={() => fileInputRef.current.click()}
              style={{ marginTop: 12 }}
            >
              Chọn ảnh từ máy
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div id="qr-reader-region-img" style={{ display: "none" }} />
          </Card>
        </Col>
        <Col
          className={`qr-cccd-scanner__result ${
            !capturedImage ? "hidden" : ""
          }`}
          xs={24}
          lg={12}
        >
          <Card
            title={
              <Typography.Title
                level={4}
                style={{ margin: 0, textAlign: "center" }}
              >
                Thông tin CCCD
              </Typography.Title>
            }
            bordered={false}
            bodyStyle={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 66,
            }}
          >
            {error && (
              <Typography.Text
                style={{ textAlign: "center", marginBottom: 12 }}
                type="danger"
              >
                {error}
              </Typography.Text>
            )}
            <CccdResultInfo
              result={result}
              capturedImage={capturedImage}
              onRetake={capturedImage ? retake : undefined}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
